import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { ReactEditor } from 'slate-react';
import { ESC } from '../../../constants/keycodes';

const TextPopupWrapper = React.memo(
  ({ editor, element, isOpen, onClose, children }) => {
    const [positionX, setPositionX] = useState(0);
    const [positionY, setPositionY] = useState(0);

    const nodeRef = useRef();
    const editorRef = useRef();

    const handleKeyDown = e => {
      if (isOpen && e.keyCode === ESC) {
        onClose();
      }
    };

    const hideModalOverlay = event => {
      event.preventDefault();
      event.stopPropagation();

      if (event.target === event.currentTarget) {
        onClose();
      }
    };

    const updatePosition = () => {
      const nodeRefCurrent = nodeRef.current;
      const editorRefCurrent = editorRef.current;
      if (nodeRefCurrent && editorRefCurrent) {
        const editorRect = editorRefCurrent.getBoundingClientRect();
        const nodeRect = nodeRefCurrent.getBoundingClientRect();

        const positionLeft = nodeRect.left - editorRect.left;
        const positionTop = nodeRect.top - editorRect.top + nodeRect.height;

        setPositionX(positionLeft);
        setPositionY(positionTop);
      }
    };

    useEffect(() => {
      nodeRef.current = ReactEditor.toDOMNode(editor, element);
      editorRef.current = document && document.getElementById(editor.id);

      document.addEventListener('keydown', handleKeyDown, false);

      return () => {
        document.removeEventListener('keydown', handleKeyDown, false);
      };
    }, []);

    useEffect(() => {
      updatePosition();
    }, [isOpen]);

    if (!isOpen) return null;

    return ReactDOM.createPortal(
      <>
        <StyledPopupOverlay onClick={hideModalOverlay} />
        <PopupWrapperContainer positionX={positionX} positionY={positionY}>
          {children}
        </PopupWrapperContainer>
      </>,
      editorRef.current
    );
  }
);

TextPopupWrapper.propTypes = {
  editor: PropTypes.objectOf(ReactEditor).isRequired,
  element: PropTypes.shape({
    url: PropTypes.string,
  }).isRequired,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  children: PropTypes.arrayOf(PropTypes.element).isRequired,
};

export const PopupWrapperContainer = styled.div`
  position: absolute;
  left: ${props => (props.positionX ? props.positionX : 0)}px;
  top: ${props => (props.positionY ? props.positionY : 0)}px;
  z-index: 20;
  margin-top: 10px;
  ::after {
    content: '';
    position: absolute;
    top: -7px;
    left: 18px;
    width: 0;
    height: 0;
    border-left: 7px solid transparent;
    border-right: 7px solid transparent;
    border-bottom: 7px solid white;
  }
`;

export const StyledPopupOverlay = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  position: fixed;
  top: 0;
  left: 0;
  overflow: auto;
  z-index: 19;
`;

export default TextPopupWrapper;
